import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Typography
} from '@mui/material';
import { format } from 'date-fns';
import CircleIcon from '@mui/icons-material/Circle';
import {
  MapStatusToColor,
  MapStatusToDisplayAs,
  MapTypeToDisplayInCard
} from '../../common/utils/enum';
import {
  EditIcon,
  cardBottom,
  cardBottomLeft,
  cardContent,
  cardMiddle,
  cardMiddleLeft,
  cardMiddleRight,
  cardStyles,
  cardTop,
  cardTopLeft,
  cardTopRight,
  dateContainer,
  dateStyles,
  editBtn,
  overflowText,
  projectTypeText,
  statusContainer,
  statusStyles,
  titleContainer,
  titleStyles,
  weeksLeftText
} from './styles/project-card-style';
import { useEffect, useState } from 'react';
import { IAvatar, IPermissions } from '../../common/utils/interfaces';
import { useAppSelector } from '../../../redux/hooks';
import AvatarGroupForProjects from './avatar-group-for-projects';
import EditProjectModal from './forms/edit-project-modal';
import { IProjectData, IProjectMemberInfo } from '../type';
import { useLazyQuery, useMutation } from '@apollo/client';
import { EDIT_PROJECT_MEMBER_START_DATE, GET_EDITED_PROJECT } from '../gql';
import { calculateTimeRemaining } from '../../common/utils/helperFunctions';

interface PropsType {
  projectData: IProjectData;
}

const ProjectCard = ({ projectData }: PropsType): JSX.Element => {
  const [project, setProject] = useState<IProjectData>(projectData);
  const [actionUserId, setActionUserId] = useState<string>('');
  const [isEditModalVisible, setEditModalVisible] = useState(false);
  const { profileData } = useAppSelector((state) => state.profileData);
  const [permissions, setPermissions] = useState<IPermissions>({
    project: { create: false, read: true, update: false, delete: false },
    project_member: { create: false, read: true, update: false, delete: false }
  });

  const timeLeft = calculateTimeRemaining(project.tentativeEndDate);

  const getMemberInfoArray = (
    projectMembers: IProjectMemberInfo[]
  ): IAvatar[] => {
    const memberInfoArray: IAvatar[] = [];
    projectMembers.forEach((member) => {
      const memberInfo = {
        providerId: member.member.providerId,
        profilePic: '',
        displayName: member.member.displayName
      };
      memberInfoArray.push(memberInfo);
    });
    return memberInfoArray;
  };

  const [getEditedProject, { data: editedProject }] = useLazyQuery(
    GET_EDITED_PROJECT,
    {
      fetchPolicy: 'network-only'
    }
  );

  const [editProjectMember] = useMutation(EDIT_PROJECT_MEMBER_START_DATE);

  const editProjectCallback = (): void => {
    void getEditedProject({
      variables: { id: project.id }
    }).then(() => {});
  };

  const updateRoleStartEndDateForMembers = (
    projectMembers: IProjectMemberInfo[],
    editedStartDate: Date
  ): void => {
    projectMembers.forEach((member) => {
      let startDate = new Date(member.roleStartDate);
      const newStartDate = new Date(editedStartDate);
      if (startDate.getTime() < newStartDate.getTime()) {
        startDate = newStartDate;
      }

      const roleIds = member.memberRoles.map((role) => role.projectRole.id);
      void editProjectMember({
        variables: {
          actionUserId,
          projectMemberId: member.id,
          hoursPerWeek: member.hoursPerWeek,
          memberId: member.member.id,
          memberStatus: member.memberStatus,
          projectRolesIds: roleIds,
          projectId: project.id,
          roleStartDate: new Date(startDate).toISOString(),
          roleEndDate: new Date(member.roleEndDate).toISOString()
        }
      });
    });
  };
  const closeRequestForm = (): void => {
    setEditModalVisible(false);
  };
  useEffect(() => {
    if (profileData) {
      if (profileData.memberId) {
        setActionUserId(profileData.memberId);
      }
    }
  }, [profileData]);

  useEffect(() => {
    if (editedProject?.getProject) {
      const editedDetails = editedProject?.getProject;
      setProject((prev) => {
        return {
          ...prev,
          projectName: editedDetails.projectName,
          clientName: editedDetails.clientName,
          tentativeStartDate: new Date(editedDetails.tentativeStartDate),
          tentativeEndDate: new Date(editedDetails.tentativeEndDate),
          hypercarePeriod: editedDetails.hypercarePeriod,
          members: editedDetails.members,
          projectType: editedDetails.projectType
        };
      });
      updateRoleStartEndDateForMembers(
        editedDetails.members,
        new Date(editedDetails.tentativeStartDate)
      );
    }
  }, [editedProject]);

  useEffect(() => {
    if (profileData) {
      if (
        profileData?.projectPermissions &&
        profileData.projectPermissions[project.id]
      ) {
        setPermissions(profileData.projectPermissions[project.id]);
      } else if (profileData.permissions) {
        setPermissions(profileData.permissions);
      }
    }
  }, [profileData]);

  return (
    <Card sx={cardStyles}>
      <CardActionArea
        href={`/projects/${project.id}`}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <CardContent sx={cardContent}>
          <Box sx={cardTop}>
            <Box sx={cardTopLeft}>
              <Box sx={titleContainer}>
                <CircleIcon
                  sx={{ fontSize: 'medium', color: project.colorCode }}
                />
                <Typography
                  sx={titleStyles}
                >{`${project.projectName}`}</Typography>
              </Box>
              <Box sx={dateContainer}>
                <Typography sx={dateStyles}>
                  {`Created On ${format(
                    new Date(project.createdAt),
                    'MM/dd/yy'
                  )} `}
                </Typography>
              </Box>
            </Box>
            <Box sx={cardTopRight}>
              <Box
                sx={{
                  ...statusContainer,
                  width: `${
                    MapStatusToDisplayAs(project.projectStatus).length > 12
                      ? 184
                      : 120
                  }px`
                }}
              >
                <CircleIcon
                  sx={{
                    fontSize: 'small',
                    color: MapStatusToColor(project.projectStatus)
                  }}
                />
                <Typography sx={statusStyles}>
                  {`${MapStatusToDisplayAs(project.projectStatus)}`}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={cardMiddle}>
            <Box sx={cardMiddleLeft}>
              <Typography sx={projectTypeText}>
                {project.projectType
                  ? MapTypeToDisplayInCard(project.projectType)
                  : 'FTFC'}
              </Typography>
            </Box>
            <Box sx={cardMiddleRight}>
              <Typography sx={weeksLeftText}>{`${timeLeft}`}</Typography>
            </Box>
          </Box>
          <Box sx={cardBottom}>
            <Box sx={cardBottomLeft}>
              <AvatarGroupForProjects
                members={getMemberInfoArray(project.members)}
                max={4}
              />
              {project.members.slice(4).length > 0 ? (
                <Typography data-testid={'overflow-avatars'} sx={overflowText}>
                  {`+${project.members.slice(4).length} People`}
                </Typography>
              ) : null}
            </Box>
            <Box>
              {profileData?.id === projectData.createdById ||
              permissions.project.update ? (
                <Button
                  variant="contained"
                  sx={editBtn}
                  onClick={(e) => {
                    e.preventDefault();
                    setEditModalVisible(true);
                  }}
                >
                  <EditIcon />
                  Edit
                </Button>
              ) : null}
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
      <EditProjectModal
        project={project}
        closeRequestForm={closeRequestForm}
        editProjectCallback={editProjectCallback}
        permissions={permissions}
        isEditModalVisible={isEditModalVisible}
      />
    </Card>
  );
};

export default ProjectCard;
